import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Checkbox,
  Alert,
} from '@mui/material';
import { Add, Delete, Edit, Refresh } from '@mui/icons-material';
import {
  getTenantSipPagingSettingsAdmin,
  upsertTenantSipPagingSettingsAdmin,
  getTenantSipPagingRolePoliciesAdmin,
  setTenantSipPagingRolePoliciesAdmin,
  getTenantSipPagingZonesAdmin,
  createTenantSipPagingZoneAdmin,
  updateTenantSipPagingZoneAdmin,
  deleteTenantSipPagingZoneAdmin,
  searchTenantUsersAdmin,
  fetchUsersFromAuth,
  getTenantSipConfig,
  listTenantSipCredentials,
  createTenantSipCredential,
  resetTenantSipCredentialPassword,
} from '../../../api/backendApi';
import TroubleshootThis from '../../../assistant/TroubleshootThis';
import {
  buildSipUri,
  extractSipUsername,
  isDuplicateSipUserError,
  parseApiErrorMessage,
  suggestSipUsername,
} from './sipPagingZoneHelpers';

export function SipZoneDialog({
  open,
  onClose,
  onSubmit,
  initial,
  tenantId,
  userLabelMap: globalUserLabelMap,
  saving = false,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [sipUri, setSipUri] = useState('');
  const [defaultDuration, setDefaultDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [allowedRolesText, setAllowedRolesText] = useState('');
  const [allowedUserIds, setAllowedUserIds] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [localUserLabelMap, setLocalUserLabelMap] = useState({});
  const [error, setError] = useState('');
  const [sipDomain, setSipDomain] = useState('');
  const [canCreateInTwilio, setCanCreateInTwilio] = useState(false);
  const [createInTwilio, setCreateInTwilio] = useState(true);
  const [uriTouched, setUriTouched] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState(null);
  const [dialogTab, setDialogTab] = useState('paging');

  const COMMON_ROLES = [
    'admin',
    'tenant_admin',
    'superintendent',
    'principal',
    'teacher',
    'responder',
  ];

  useEffect(() => {
    const z = initial || {};
    setName(z.name || '');
    setDescription(z.description || '');
    setActive(z.active !== false);
    setSipUri(z.sipUri || '');
    setDefaultDuration(
      typeof z.defaultDurationSeconds === 'number' && !Number.isNaN(z.defaultDurationSeconds)
        ? String(z.defaultDurationSeconds)
        : ''
    );
    setMaxDuration(
      typeof z.maxDurationSeconds === 'number' && !Number.isNaN(z.maxDurationSeconds)
        ? String(z.maxDurationSeconds)
        : ''
    );
    const zoneRoles = Array.isArray(z.allowedRoles) ? z.allowedRoles : [];
    if (!initial || zoneRoles.length === 0) {
      const defaultRoles = ['admin', 'tenant_admin'];
      setSelectedRoles(defaultRoles);
      setAllowedRolesText('');
    } else {
      const commonSelected = zoneRoles.filter((r) => COMMON_ROLES.includes(r));
      const extraRoles = zoneRoles.filter((r) => !COMMON_ROLES.includes(r));
      setSelectedRoles(commonSelected);
      setAllowedRolesText(extraRoles.join(', '));
    }
    setAllowedUserIds(Array.isArray(z.allowedUserIds) ? z.allowedUserIds : []);
    setUserSearchTerm('');
    setUserSearchResults([]);
    setLocalUserLabelMap({});
    setError('');
    setUriTouched(Boolean(z.sipUri));
    setCreateInTwilio(!z.id);
    setProvisioning(false);
    setResettingPassword(false);
    setRevealedPassword(null);
    setDialogTab('paging');
  }, [initial, open]);

  useEffect(() => {
    let activeFlag = true;
    const loadSipConfig = async () => {
      if (!open || !tenantId) {
        setSipDomain('');
        setCanCreateInTwilio(false);
        return;
      }
      try {
        const config = await getTenantSipConfig(tenantId);
        if (!activeFlag) return;
        const domain = String(config?.domainFqdn || config?.domain || '').trim();
        const hasCredList = Boolean(config?.credentialListSid);
        setSipDomain(domain);
        const ready = Boolean(domain && hasCredList);
        setCanCreateInTwilio(ready);
        if (!ready) {
          setCreateInTwilio(false);
        }
      } catch {
        if (!activeFlag) return;
        setSipDomain('');
        setCanCreateInTwilio(false);
        setCreateInTwilio(false);
      }
    };
    loadSipConfig();
    return () => {
      activeFlag = false;
    };
  }, [open, tenantId]);

  useEffect(() => {
    if (!open || !sipDomain || uriTouched || initial?.sipUri) return;
    const suggested = suggestSipUsername(name);
    if (suggested) setSipUri(buildSipUri(suggested, sipDomain));
  }, [open, sipDomain, name, uriTouched, initial]);

  useEffect(() => {
    let activeFlag = true;
    const runSearch = async () => {
      const term = userSearchTerm.trim();
      if (!open || !tenantId || term.length < 2) {
        setUserSearchResults([]);
        return;
      }
      setUserSearchLoading(true);
      try {
        const results = await searchTenantUsersAdmin(tenantId, term, 25);
        if (!activeFlag) return;
        const list = Array.isArray(results) ? results : [];
        setUserSearchResults(list);

        // Cache friendly labels for any users returned by search
        setLocalUserLabelMap((prev) => {
          const next = { ...prev };
          list.forEach((u) => {
            const uid = u.id;
            if (!uid) return;
            const label =
              u.displayName ||
              [u.firstName, u.lastName].filter(Boolean).join(' ') ||
              u.email ||
              uid;
            const email = u.email || '';
            next[uid] = { label, email };
          });
          return next;
        });
      } catch (e) {
        if (!activeFlag) return;
        setUserSearchResults([]);
      } finally {
        if (activeFlag) setUserSearchLoading(false);
      }
    };
    const id = setTimeout(runSearch, 300);
    return () => {
      activeFlag = false;
      clearTimeout(id);
    };
  }, [userSearchTerm, tenantId, open]);

  // When opening an existing zone, resolve display labels for any pre-assigned user IDs
  useEffect(() => {
    if (!open) return;
    if (!Array.isArray(allowedUserIds) || allowedUserIds.length === 0) return;

    setLocalUserLabelMap((prev) => {
      const next = { ...prev };
      allowedUserIds.forEach((uid) => {
        if (!uid) return;
        const globalInfo = globalUserLabelMap && globalUserLabelMap[uid];
        if (globalInfo && !next[uid]) {
          next[uid] = globalInfo;
        }
      });
      return next;
    });
  }, [open, allowedUserIds, globalUserLabelMap]);

  const validateSipUri = (uri) => {
    if (!uri) return 'SIP URI is required';
    const trimmed = uri.trim();
    
    // Check for sip: prefix
    if (!trimmed.toLowerCase().startsWith('sip:')) {
      return 'SIP URI must start with "sip:" (e.g., sip:device@domain.sip.twilio.com)';
    }
    
    // Remove sip: prefix for further validation
    const withoutPrefix = trimmed.substring(4);
    
    // Check for exactly one @ symbol
    const atCount = (withoutPrefix.match(/@/g) || []).length;
    if (atCount === 0) {
      return 'SIP URI must contain @ symbol (e.g., sip:device@domain.sip.twilio.com)';
    }
    if (atCount > 1) {
      return 'SIP URI must contain only one @ symbol. Check for typos (e.g., use "." not "@" between domain parts)';
    }
    
    // Check that there's content before and after @
    const [user, domain] = withoutPrefix.split('@');
    if (!user || !user.trim()) {
      return 'SIP URI must have a user/device name before @ (e.g., sip:device@domain.sip.twilio.com)';
    }
    if (!domain || !domain.trim()) {
      return 'SIP URI must have a domain after @ (e.g., sip:device@domain.sip.twilio.com)';
    }
    
    // Check domain has at least one dot (basic domain validation)
    if (!domain.includes('.')) {
      return 'SIP URI domain should include a valid domain (e.g., ajax.sip.twilio.com)';
    }
    
    return null; // Valid
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setDialogTab('paging');
      setError('Name is required');
      return;
    }

    let nextUri = sipUri.trim();
    if (!nextUri && sipDomain) {
      const suggested = suggestSipUsername(name);
      if (suggested) {
        nextUri = buildSipUri(suggested, sipDomain);
        setSipUri(nextUri);
      }
    }

    const sipUriError = validateSipUri(nextUri);
    if (sipUriError) {
      setDialogTab('paging');
      setError(sipUriError);
      return;
    }

    const extraRoles = allowedRolesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const allowedRoles = Array.from(new Set([...selectedRoles, ...extraRoles]));

    const payload = {
      name: name.trim(),
      description: description || '',
      active: !!active,
      sipUri: nextUri,
      allowedRoles,
      allowedUserIds,
    };

    const defDur = Number(defaultDuration);
    if (!Number.isNaN(defDur) && defDur > 0) payload.defaultDurationSeconds = defDur;
    const maxDur = Number(maxDuration);
    if (!Number.isNaN(maxDur) && maxDur > 0) payload.maxDurationSeconds = maxDur;

    let twilioCredential = null;
    const shouldProvision = createInTwilio && !isEdit;
    if (shouldProvision) {
      const username = extractSipUsername(nextUri);
      if (!username) {
        setError('SIP URI must include a username to create the Twilio SIP user');
        return;
      }
      setProvisioning(true);
      try {
        let existing = [];
        try {
          existing = await listTenantSipCredentials(tenantId);
        } catch {
          existing = [];
        }
        const already = (Array.isArray(existing) ? existing : []).find(
          (item) => String(item?.username || '').trim() === username
        );
        if (already) {
          twilioCredential = {
            ...already,
            reused: true,
            password: null,
          };
        } else {
          twilioCredential = await createTenantSipCredential(tenantId, {
            username,
            label: name.trim(),
          });
        }
      } catch (e) {
        if (isDuplicateSipUserError(e)) {
          twilioCredential = { username, reused: true, password: null };
        } else {
          setError(
            parseApiErrorMessage(e) ||
              'Failed to create the SIP user in Twilio. The paging zone was not created.'
          );
          setProvisioning(false);
          return;
        }
      } finally {
        setProvisioning(false);
      }
    }

    try {
      await onSubmit(payload, { twilioCredential });
    } catch (e) {
      setError(parseApiErrorMessage(e) || 'Failed to save SIP paging zone');
    }
  };

  const handleResetPassword = async () => {
    const username = extractSipUsername(sipUri);
    if (!username) {
      setError('SIP URI must include a username to generate a password');
      return;
    }
    const ok = window.confirm(
      `Generate a new Twilio SIP password for ${username}? The current password will stop working. Copy the new password immediately — it is shown only once.`
    );
    if (!ok) return;

    setError('');
    setResettingPassword(true);
    try {
      const data = await resetTenantSipCredentialPassword(tenantId, { username });
      if (!data?.password) {
        throw new Error('The server did not return a SIP password');
      }
      setRevealedPassword({
        username: data.username || username,
        password: data.password,
      });
    } catch (e) {
      setError(
        parseApiErrorMessage(e) ||
          'Failed to generate a new SIP password. The username must exist in Twilio for this tenant.'
      );
    } finally {
      setResettingPassword(false);
    }
  };

  const isEdit = !!initial?.id;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0 }}>
        {isEdit ? 'Edit SIP Paging Zone' : 'Add SIP Paging Zone'}
        <Tabs
          value={dialogTab}
          onChange={(_event, value) => setDialogTab(value)}
          sx={{ mt: 1 }}
        >
          <Tab label="SIP Paging" value="paging" />
          <Tab label="Assignment" value="assignment" />
        </Tabs>
      </DialogTitle>
      <DialogContent dividers>
        {dialogTab === 'paging' && (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Zone Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!error}
            helperText={error || ''}
          />

          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <TextField
            label="SIP URI"
            fullWidth
            value={sipUri}
            onChange={(e) => {
              setUriTouched(true);
              setSipUri(e.target.value);
              if (error && error.includes('SIP URI')) setError('');
            }}
            onBlur={() => {
              const raw = sipUri.trim();
              if (!raw || raw.includes('@') || !sipDomain) return;
              const username = extractSipUsername(raw) || suggestSipUsername(raw);
              if (username) {
                setSipUri(buildSipUri(username, sipDomain));
              }
            }}
            placeholder={
              sipDomain ? `sip:lockdown@${sipDomain}` : 'sip:device@domain.sip.twilio.com'
            }
            error={!!error && error.includes('SIP URI')}
            helperText={
              (error && error.includes('SIP URI'))
                ? error
                : sipDomain
                  ? `Format: sip:device@${sipDomain}`
                  : 'Format: sip:device@domain.sip.twilio.com (must have exactly one @ symbol)'
            }
          />

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              SIP username and password
            </Typography>
            <Stack spacing={1.5}>
              <TextField
                label="SIP username"
                fullWidth
                value={extractSipUsername(sipUri) || ''}
                InputProps={{ readOnly: true }}
                helperText="Taken from the SIP URI."
              />
              <TextField
                label="SIP password"
                fullWidth
                value={revealedPassword?.password || ''}
                placeholder="Shown once after you generate it"
                InputProps={{ readOnly: true }}
              />
              {isEdit && (
                <Box>
                  <Button
                    variant="outlined"
                    onClick={handleResetPassword}
                    disabled={resettingPassword || provisioning || saving}
                  >
                    {resettingPassword ? 'Generating…' : 'Generate new SIP password'}
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    Replaces the Twilio password for this SIP user. The previous password
                    stops working. The new password is shown once.
                  </Typography>
                </Box>
              )}
              {error && !error.includes('SIP URI') && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}
            </Stack>
          </Box>

          {!isEdit && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!createInTwilio}
                    disabled={!canCreateInTwilio || provisioning || saving}
                    onChange={(e) => setCreateInTwilio(e.target.checked)}
                  />
                }
                label="Also create this SIP user in Twilio"
              />
              <Typography variant="body2" color="text.secondary">
                {canCreateInTwilio
                  ? `Uses the tenant SIP domain ${sipDomain} and the existing Twilio credential-create API.`
                  : 'Tenant SIP Interworking needs a domain and credential list before Twilio users can be created here. The paging zone can still be saved.'}
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
              control={<Switch checked={!!active} onChange={(e) => setActive(e.target.checked)} />}
              label="Active"
            />
            <TextField
              label="Default Duration (seconds)"
              type="number"
              value={defaultDuration}
              onChange={(e) => setDefaultDuration(e.target.value)}
              sx={{ width: 220 }}
            />
            <TextField
              label="Max Duration (seconds)"
              type="number"
              value={maxDuration}
              onChange={(e) => setMaxDuration(e.target.value)}
              sx={{ width: 220 }}
            />
          </Stack>

        </Stack>
        )}
        {dialogTab === 'assignment' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Allowed Users
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              People in this list can start SIP pages for this zone. Admin and tenant admin can also start them.
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <TextField
                label="Search users by name or email"
                fullWidth
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                helperText="Type at least 2 characters to search"
              />
              {userSearchLoading && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
            </Stack>
            {userSearchResults.length > 0 && (
              <Box
                sx={{
                  maxHeight: 240,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  p: 1,
                }}
              >
                {userSearchResults.map((u) => {
                  const uid = u.id;
                  const already = allowedUserIds.includes(uid);
                  const label =
                    u.displayName ||
                    [u.firstName, u.lastName].filter(Boolean).join(' ') ||
                    u.email ||
                    uid;
                  return (
                    <Stack
                      key={uid}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ py: 0.5 }}
                    >
                      <Checkbox
                        size="small"
                        checked={already}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAllowedUserIds((prev) => {
                            if (checked) {
                              if (prev.includes(uid)) return prev;
                              return [...prev, uid];
                            }
                            return prev.filter((id) => id !== uid);
                          });
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {label}
                        {u.email && label !== u.email && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            {u.email}
                          </Typography>
                        )}
                      </Typography>
                    </Stack>
                  );
                })}
              </Box>
            )}
            {allowedUserIds.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {allowedUserIds.map((uid) => {
                  const info = localUserLabelMap[uid] || (globalUserLabelMap && globalUserLabelMap[uid]);
                  const label = info?.label || uid;
                  return (
                    <Chip
                      key={uid}
                      label={label}
                      size="small"
                      onDelete={() =>
                        setAllowedUserIds((prev) => prev.filter((id) => id !== uid))
                      }
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users assigned yet.
              </Typography>
            )}
          </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={provisioning || saving || resettingPassword}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={provisioning || saving || resettingPassword}
        >
          {provisioning
            ? 'Creating Twilio user…'
            : saving
              ? 'Saving…'
              : isEdit
                ? 'Save Changes'
                : 'Create Zone'}
        </Button>
      </DialogActions>
      <Dialog
        open={!!revealedPassword?.password}
        onClose={() => setRevealedPassword(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>New SIP password</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This password is shown once. Copy it now for the SIP endpoint that will
            register as this paging zone.
          </Alert>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Stack spacing={1.5}>
              <TextField
                label="SIP username"
                fullWidth
                value={revealedPassword?.username || ''}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="SIP password"
                fullWidth
                value={revealedPassword?.password || ''}
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(revealedPassword?.password || '');
              } catch {
                // ignore clipboard failures; the password remains visible
              }
            }}
          >
            Copy password
          </Button>
          <Button variant="contained" onClick={() => setRevealedPassword(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default function SipPagingTab({ tenantId }) {
  const [settings, setSettings] = useState({ sipPagingEnabled: true });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState('');

  const [rolePolicies, setRolePolicies] = useState({});
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [rolesError, setRolesError] = useState('');

  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zonesError, setZonesError] = useState('');

  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [zoneDialogInitial, setZoneDialogInitial] = useState(null);
  const [zoneDialogSaving, setZoneDialogSaving] = useState(false);
  const [createdTwilioCredential, setCreatedTwilioCredential] = useState(null);

  const [userLabelMap, setUserLabelMap] = useState({});

  const SHOW_SIP_ROLE_POLICIES = false;

  useEffect(() => {
    if (!tenantId) return;
    loadAll();
  }, [tenantId]);

  const loadAll = async () => {
    await Promise.all([loadSettings(), loadZones(), loadRolePolicies()]);
  };

  const loadSettings = async () => {
    if (!tenantId) return;
    setSettingsLoading(true);
    setSettingsError('');
    try {
      const data = await getTenantSipPagingSettingsAdmin(tenantId);
      setSettings({
        sipPagingEnabled: data.sipPagingEnabled !== false,
        globalDefaultDurationSeconds: data.globalDefaultDurationSeconds || '',
        globalMaxDurationSeconds: data.globalMaxDurationSeconds || '',
      });
    } catch (e) {
      setSettingsError(String(e?.message || 'Failed to load SIP paging settings'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!tenantId) return;
    setSettingsSaving(true);
    setSettingsError('');
    try {
      const payload = {
        sipPagingEnabled: !!settings.sipPagingEnabled,
      };
      const defDur = Number(settings.globalDefaultDurationSeconds);
      if (!Number.isNaN(defDur) && defDur > 0) payload.globalDefaultDurationSeconds = defDur;
      const maxDur = Number(settings.globalMaxDurationSeconds);
      if (!Number.isNaN(maxDur) && maxDur > 0) payload.globalMaxDurationSeconds = maxDur;
      await upsertTenantSipPagingSettingsAdmin(tenantId, payload);
      await loadSettings();
    } catch (e) {
      setSettingsError(String(e?.message || 'Failed to save SIP paging settings'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const loadRolePolicies = async () => {
    if (!tenantId) return;
    setRolesLoading(true);
    setRolesError('');
    try {
      const roles = await getTenantSipPagingRolePoliciesAdmin(tenantId);
      setRolePolicies(roles || {});
    } catch (e) {
      setRolesError(String(e?.message || 'Failed to load SIP role policies'));
    } finally {
      setRolesLoading(false);
    }
  };

  const saveRolePolicies = async () => {
    if (!tenantId) return;
    setRolesSaving(true);
    setRolesError('');
    try {
      await setTenantSipPagingRolePoliciesAdmin(tenantId, rolePolicies || {});
      await loadRolePolicies();
    } catch (e) {
      setRolesError(String(e?.message || 'Failed to save SIP role policies'));
    } finally {
      setRolesSaving(false);
    }
  };

  const loadZones = async () => {
    if (!tenantId) return;
    setZonesLoading(true);
    setZonesError('');
    try {
      const list = await getTenantSipPagingZonesAdmin(tenantId);
      const zonesList = Array.isArray(list) ? list : [];
      setZones(zonesList);

      // Hydrate friendly labels for users in this tenant so the table can show names
      try {
        const resp = await fetchUsersFromAuth({ tenantId });
        let users = [];
        if (Array.isArray(resp)) users = resp;
        else if (resp?.data && Array.isArray(resp.data)) users = resp.data;
        else if (resp?.data && Array.isArray(resp.data.users)) users = resp.data.users;
        else if (Array.isArray(resp?.users)) users = resp.users;

        const map = {};
        users.forEach((u) => {
          const uid = u.id || u.uid;
          if (!uid) return;
          const label =
            u.displayName ||
            [u.firstName, u.lastName].filter(Boolean).join(' ') ||
            u.email ||
            uid;
          const email = u.email || '';
          map[uid] = { label, email };
        });
        setUserLabelMap(map);
      } catch {
        // If user lookup fails, fall back to showing raw IDs in the table
      }
    } catch (e) {
      setZonesError(String(e?.message || 'Failed to load SIP paging zones'));
    } finally {
      setZonesLoading(false);
    }
  };

  const handleAddZone = () => {
    setZoneDialogInitial(null);
    setZoneDialogOpen(true);
  };

  const handleEditZone = async (zone) => {
    setZoneDialogInitial(zone);
    setZoneDialogOpen(true);
  };

  const handleDeleteZone = async (zone) => {
    if (!tenantId || !zone?.id) return;
    const ok = window.confirm(`Delete SIP paging zone "${zone.name || zone.id}"?`);
    if (!ok) return;
    setZonesLoading(true);
    setZonesError('');
    try {
      await deleteTenantSipPagingZoneAdmin(tenantId, zone.id);
      await loadZones();
    } catch (e) {
      setZonesError(String(e?.message || 'Failed to delete SIP paging zone'));
    } finally {
      setZonesLoading(false);
    }
  };

  const handleZoneSubmit = async (payload, extras = {}) => {
    if (!tenantId) return;
    setZoneDialogSaving(true);
    setZonesError('');
    try {
      if (zoneDialogInitial && zoneDialogInitial.id) {
        await updateTenantSipPagingZoneAdmin(tenantId, zoneDialogInitial.id, payload);
      } else {
        await createTenantSipPagingZoneAdmin(tenantId, payload);
      }
      await loadZones();
      setZoneDialogOpen(false);
      if (extras.twilioCredential?.password) {
        setCreatedTwilioCredential(extras.twilioCredential);
      }
    } catch (e) {
      const message = parseApiErrorMessage(e) || 'Failed to save SIP paging zone';
      setZonesError(
        extras.twilioCredential?.password
          ? `${message} The Twilio SIP user was created. Copy the password now: ${extras.twilioCredential.password}`
          : message
      );
      throw e;
    } finally {
      setZoneDialogSaving(false);
    }
  };

  const handleAddRole = () => {
    setRolePolicies((prev) => {
      const next = { ...(prev || {}) };
      let base = 'new-role';
      let key = base;
      let i = 1;
      while (next[key]) {
        key = `${base}-${i++}`;
      }
      next[key] = { canPage: true };
      return next;
    });
  };

  const handleDeleteRole = (roleKey) => {
    setRolePolicies((prev) => {
      const next = { ...(prev || {}) };
      delete next[roleKey];
      return next;
    });
  };

  const handleRoleNameChange = (oldKey, newKey) => {
    setRolePolicies((prev) => {
      const src = prev || {};
      const value = src[oldKey] || {};
      const next = { ...src };
      delete next[oldKey];
      const trimmed = newKey.trim();
      if (trimmed) {
        next[trimmed] = value;
      }
      return next;
    });
  };

  const handleRoleFieldChange = (roleKey, field, value) => {
    setRolePolicies((prev) => {
      const src = prev || {};
      const current = src[roleKey] || {};
      return {
        ...src,
        [roleKey]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const roleEntries = Object.entries(rolePolicies || {});

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3">
            Tenant SIP Paging Settings
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={loadSettings}
              disabled={settingsLoading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={saveSettings}
              disabled={settingsSaving || settingsLoading}
            >
              {settingsSaving ? 'Saving…' : 'Save Settings'}
            </Button>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!settings.sipPagingEnabled}
                onChange={(e) => setSettings((prev) => ({ ...prev, sipPagingEnabled: e.target.checked }))}
              />
            }
            label="SIP Paging Enabled"
          />
          {settingsLoading && <CircularProgress size={20} />}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
          <TextField
            label="Global Default Duration (seconds)"
            type="number"
            sx={{ width: 260 }}
            value={settings.globalDefaultDurationSeconds}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, globalDefaultDurationSeconds: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Global Max Duration (seconds)"
            type="number"
            sx={{ width: 260 }}
            value={settings.globalMaxDurationSeconds}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, globalMaxDurationSeconds: e.target.value }))
            }
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Enable SIP paging to allow users to start live SIP pages. Per-zone access is controlled
          by the role policies and zone allowlists below.
        </Typography>
        {settingsError && (
          <Typography variant="body2" color="error">
            {settingsError}
          </Typography>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3">
            SIP Paging Zones
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              startIcon={<Refresh />}
              onClick={loadZones}
              disabled={zonesLoading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddZone}
              disabled={!tenantId}
            >
              Add SIP Zone
            </Button>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Each zone points to a SIP URI. Users must have a paging-enabled role and be allowed by
          role or user in the zone to see it in HomeLauncher.
        </Typography>
        {zonesError && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {zonesError}
          </Typography>
        )}
        {zonesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={26} />
          </Box>
        ) : zones.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No SIP paging zones configured yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Durations</TableCell>
                <TableCell>Users</TableCell>
                <TableCell>SIP URI</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {zones.map((z) => (
                <TableRow key={z.id} hover>
                  <TableCell>{z.name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={z.active === false ? 'inactive' : 'active'}
                      color={z.active === false ? 'default' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {z.defaultDurationSeconds ? `Default ${z.defaultDurationSeconds}s` : 'Default —'}
                    </Typography>
                    <Typography variant="body2">
                      {z.maxDurationSeconds ? `Max ${z.maxDurationSeconds}s` : 'Max —'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {Array.isArray(z.allowedUserIds) && z.allowedUserIds.length > 0 ? (
                      z.allowedUserIds.map((uid) => {
                        const info = userLabelMap[uid];
                        const label = info?.label || uid;
                        return (
                          <Chip
                            key={uid}
                            label={label}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        );
                      })
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{z.sipUri || '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                      <TroubleshootThis pins={{ zoneId: z.id, kind: 'sip', label: z.name || z.label || z.id }} />
                      <IconButton size="small" aria-label="Edit" onClick={() => handleEditZone(z)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" aria-label="Delete" onClick={() => handleDeleteZone(z)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {SHOW_SIP_ROLE_POLICIES && (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid #e5e7eb' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6" component="h3">
              Role-Based SIP Paging Policies
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={loadRolePolicies}
                disabled={rolesLoading}
              >
                Refresh
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddRole}
              >
                Add Role
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={saveRolePolicies}
                disabled={rolesSaving || rolesLoading}
              >
                {rolesSaving ? 'Saving…' : 'Save Policies'}
              </Button>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            These policies decide which roles can start SIP pages and their default and maximum
            durations. Zones then further restrict which of those roles can page each zone.
          </Typography>
          {rolesError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {rolesError}
            </Typography>
          )}
          {rolesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : roleEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No SIP paging role policies configured yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Role</TableCell>
                  <TableCell>Can Page</TableCell>
                  <TableCell>Default Duration (seconds)</TableCell>
                  <TableCell>Max Duration (seconds)</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roleEntries.map(([roleKey, policy], idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <TextField
                        size="small"
                        value={roleKey}
                        onChange={(e) => handleRoleNameChange(roleKey, e.target.value)}
                        placeholder="admin, tenant_admin, superintendent, principal, teacher"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!policy?.canPage}
                        onChange={(e) => handleRoleFieldChange(roleKey, 'canPage', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={policy?.defaultDurationSeconds ?? ''}
                        onChange={(e) =>
                          handleRoleFieldChange(
                            roleKey,
                            'defaultDurationSeconds',
                            e.target.value === '' ? undefined : Number(e.target.value),
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={policy?.maxDurationSeconds ?? ''}
                        onChange={(e) =>
                          handleRoleFieldChange(
                            roleKey,
                            'maxDurationSeconds',
                            e.target.value === '' ? undefined : Number(e.target.value),
                          )
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleDeleteRole(roleKey)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <SipZoneDialog
        open={zoneDialogOpen}
        onClose={() => setZoneDialogOpen(false)}
        onSubmit={handleZoneSubmit}
        initial={zoneDialogInitial}
        tenantId={tenantId}
        userLabelMap={userLabelMap}
        saving={zoneDialogSaving}
      />

      <Dialog
        open={!!createdTwilioCredential?.password}
        onClose={() => setCreatedTwilioCredential(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Twilio SIP user created</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This password is shown once. Copy it now for the SIP endpoint that will
            register as this paging zone.
          </Alert>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Stack spacing={1.5}>
              <TextField
                label="SIP username"
                fullWidth
                value={createdTwilioCredential?.username || ''}
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="SIP password"
                fullWidth
                value={createdTwilioCredential?.password || ''}
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(createdTwilioCredential?.password || '');
              } catch {
                // ignore clipboard failures; the password remains visible
              }
            }}
          >
            Copy password
          </Button>
          <Button variant="contained" onClick={() => setCreatedTwilioCredential(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
